import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { CommunityListItem } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { useAuthGate } from "../auth/AuthProvider";

export function useJoinCommunity() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (communityId: string, opts: { open?: boolean } = {}) => {
    await api.post(`/api/communities/${communityId}/join`);
    await qc.invalidateQueries({ queryKey: ["my-communities"] });
    void qc.invalidateQueries({ queryKey: ["community"] });
    void qc.invalidateQueries({ queryKey: ["discover"] });
    if (opts.open) navigate(`/c/${communityId}`);
  };
}

export function CommunityJoinButton({ community, size = "md" }: { community: Pick<CommunityListItem, "id" | "isMember" | "visibility">; size?: "sm" | "md" }) {
  const join = useJoinCommunity();
  const gate = useAuthGate();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  if (community.isMember)
    return (
      <Button
        size={size}
        variant="outline"
        onClick={(e) => {
          e.preventDefault();
          navigate(`/c/${community.id}`);
        }}
      >
        Open
      </Button>
    );
  if (community.visibility === "private") return null;
  return (
    <Button
      size={size}
      variant="primary"
      loading={pending}
      onClick={(e) => {
        e.preventDefault();
        gate(async () => {
          setPending(true);
          try {
            await join(community.id, { open: true });
          } catch (err) {
            toast.error(errorMessage(err, "Couldn't join"));
          } finally {
            setPending(false);
          }
        })();
      }}
    >
      Join
    </Button>
  );
}
